import {useIntl} from "react-intl";
import {AutoResizeTextarea, Modal, PopupProvider} from "../../UIElements";
import React, {useEffect, useState} from "react";
import {useSupportBotContext} from "./SupportBotData";
import {
    useVersionDifferences,
    VersionDifferenceIndicator
} from "./components/VersionDifferences";
import RequestTypeToggle from "./components/RequestTypeToggle";
import {getTopicDisplayName} from "../../utils/topicUtils";
import "./BotInstructions.scss";
import {
    PanelTopClose,
    PanelTopOpen,
    ChevronRight,
    Tag,
    Trash,
    AlertCircle,
    Plus, CopyX,
    BookOpenCheck, X,
    Info
} from "lucide-react";
import SupportTopicCreator from "./components/SupportTopicCreator";

const BotInstructions = ({ botData, updateBotData, updateNestedData }) => {
    const intl = useIntl();
    const { generalInstructions, supportTopics } = botData;
    const { data: { supportTopicCodes } } = useSupportBotContext();

    const {
        isBotFieldDifferent,
        isTopicFieldDifferent,
        isTopicAddedFromDefault,
        getMissingTopics
    } = useVersionDifferences();

    const [collapsedTopics, setCollapsedTopics] = useState({});
    const [allCollapsed, setAllCollapsed] = useState(false);
    const [showMissingTopics, setShowMissingTopics] = useState(false);

    // Get missing topics that exist in default but not in current version
    const missingTopics = getMissingTopics({ config: botData });

    useEffect(()=>{
        toggleAllTopics(supportTopics?.length > 5)
    },[])

    const toggleTopicCollapse = (topicId) => {
        setCollapsedTopics(prev => ({
            ...prev,
            [topicId]: !prev[topicId]
        }));
    };

    const toggleAllTopics = () => {
        const newState = !allCollapsed;
        setAllCollapsed(newState);

        // Create a new object with all topics set to the new state
        const newCollapsedState = {};
        supportTopics.forEach(topicId => {
            newCollapsedState[topicId] = newState;
        });

        setCollapsedTopics(newCollapsedState);
    };

    const getTopicTitle = (topicId) => {
        const topicData = botData.topicData?.[topicId] || supportTopicCodes?.[topicId];
        return getTopicDisplayName(topicId, topicData, intl);
    };

    const getTopicInstructions = (topicId) => {
        if (botData.topicSettings?.[topicId] && 'instructions' in botData.topicSettings[topicId]) {
            return botData.topicSettings[topicId].instructions;
        }
        return supportTopicCodes?.[topicId]?.instructions || '';
    };

    const getTopicRequestType = (topicId) => {
        if (botData.topicSettings?.[topicId]?.requesttype) {
            return botData.topicSettings[topicId].requesttype;
        }
        return supportTopicCodes?.[topicId]?.requesttype || 'requestWithConsent';
    };

    const updateTopicField = (topicId, field, value) => {
        const currentSettings = botData.topicSettings?.[topicId] || {
            instructions: supportTopicCodes?.[topicId]?.instructions || '',
            requesttype: supportTopicCodes?.[topicId]?.requesttype || 'requestWithConsent',
            followuptype: supportTopicCodes?.[topicId]?.followuptype || ''
        };

        updateNestedData("topicSettings", topicId, {
            ...currentSettings,
            [field]: value
        });
    };

    const handleAddMissingTopic = (topicId) => {
        if (topicId && supportTopicCodes[topicId] && !supportTopics.includes(topicId)) {
            const newSupportTopics = [...supportTopics, topicId];
            updateBotData("supportTopics", newSupportTopics);

            // Initialize the full topic object if available
            if (supportTopicCodes[topicId]) {
                updateNestedData("topicData", topicId, { ...supportTopicCodes[topicId] });
            }
        }
    };

    const [deleteConfirmation, setDeleteConfirmation] = useState({
        isOpen: false,
        index: null
    });

    const openDeleteConfirmation = (index) => {
        setDeleteConfirmation({
            isOpen: true,
            index
        });
    };

    const closeDeleteConfirmation = () => {
        setDeleteConfirmation({
            isOpen: false,
            index: null
        });
    };

    const removeTopic = () => {
        const { index } = deleteConfirmation
        const topicToRemove = supportTopics[index]

        const updatedMaterials = supportTopics.filter((_, i) => i !== index)

        const updatedTopicData = { ...botData?.topicData }
        delete updatedTopicData[topicToRemove]

        updateBotData("supportTopics", updatedMaterials)
        updateBotData("topicData", updatedTopicData)

        closeDeleteConfirmation()
    }

    const isGeneralInstructionsDifferent = isBotFieldDifferent(
        { config: botData },
        "config.generalInstructions"
    );

    const lockRequestTypes = !botData.supportRequestTarget;

    return (
        <>
            <h4 className="field-header">
                {intl.formatMessage({
                    id: 'bot.instructions.general',
                    defaultMessage: 'Bot specific instructions',
                })}
                {isGeneralInstructionsDifferent && (
                    <VersionDifferenceIndicator />
                )}
            </h4>
            <AutoResizeTextarea
                id="general-instructions"
                value={generalInstructions || ""}
                onChange={e => updateBotData("generalInstructions", e.target.value)}
                placeholder={intl.formatMessage({
                    id: 'bot.instructions.general.placeholder',
                    defaultMessage: 'Enter general instructions specific for this bot',
                })}
                className={isGeneralInstructionsDifferent ? "version-different" : ""}
                rows={4}
                maxRows={26}
            />

            <div className={"sub-group"}>
                <div className={"section-header"}>
                    <h4>
                        <Tag />
                        {intl.formatMessage({
                            id: 'supportTopics',
                            defaultMessage: 'Instructions for Support Topics',
                        })}
                    </h4>
                    <div className="header-controls">
                        {supportTopics?.length > 5 && (
                            <button
                                className={"icon-button toggle"}
                                onClick={toggleAllTopics}
                                title={!allCollapsed ?
                                    intl.formatMessage({
                                        id: 'collapseAll',
                                        defaultMessage: 'Collapse All'
                                    }) :
                                    intl.formatMessage({
                                        id: 'expandAll',
                                        defaultMessage: 'Expand All'
                                    })
                                }
                            >
                                {!allCollapsed ?
                                    <PanelTopClose size={20} /> :
                                    <PanelTopOpen size={20} />
                                }
                            </button>
                        )}
                    </div>
                </div>

                {lockRequestTypes && (
                    <div className="request-type-locked-notice">
                        <Info size={16} />
                        {intl.formatMessage({
                            id: 'bot.instructions.requestTypeLocked',
                            defaultMessage: 'Support requests are disabled because this module has no support request target. To enable them, add a support request target in the Information tab.',
                        })}
                    </div>
                )}

                {supportTopics.map((topicId, i) => {
                    const isCollapsed = collapsedTopics[topicId] === true;
                    const instructions = getTopicInstructions(topicId);
                    const requestType = getTopicRequestType(topicId);

                    const isInstructionsDifferent = isTopicFieldDifferent(
                        topicId,
                        "instructions",
                        instructions,
                        botData
                    );

                    const isRequestTypeDifferent = isTopicFieldDifferent(
                        topicId,
                        "requesttype",
                        requestType,
                        botData
                    );

                    const isTopicAdded = isTopicAddedFromDefault({ config: botData }, topicId);

                    const hasAnyDifference = isInstructionsDifferent || isRequestTypeDifferent || isTopicAdded;

                    const differenceTitle = hasAnyDifference
                        ? getTopicDifferenceMessage(intl, isInstructionsDifferent, isRequestTypeDifferent, isTopicAdded)
                        : "";

                    return (
                        <div key={i} className={`topic-card ${isCollapsed ? 'collapsed' : ''} ${hasAnyDifference ? 'version-different' : ''}`}>
                            <div className={"topic-header"} onClick={() => toggleTopicCollapse(topicId)}>
                                <div className="topic-toggle">
                                    <ChevronRight
                                        size={16}
                                        style={{
                                            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                                        }}
                                    />
                                </div>
                                <label>
                                    {getTopicTitle(topicId)}
                                    {instructions && (
                                        <span className="has-instructions-indicator" title={intl.formatMessage({
                                            id: 'bot.instructions.hasInstructions',
                                            defaultMessage: 'Has specific instructions',
                                        })}>
                                            <BookOpenCheck size={20} />
                                        </span>
                                    )}
                                    {hasAnyDifference && (
                                        <VersionDifferenceIndicator differenceTitle={differenceTitle}/>
                                    )}
                                </label>
                                <RequestTypeToggle
                                    topicId={topicId}
                                    requestType={requestType}
                                    onChange={updateTopicField}
                                    onClick={(e) => e.stopPropagation()}
                                    isDifferent={isRequestTypeDifferent}
                                    locked={lockRequestTypes}
                                />
                                <button
                                    className={"remove-material"}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        openDeleteConfirmation(i)
                                    }}
                                    title={intl.formatMessage({
                                        id: 'survey.remove',
                                        defaultMessage: 'Remove',
                                    })}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <AutoResizeTextarea
                                id={`support-${i}`}
                                value={instructions}
                                onChange={e => updateTopicField(topicId, "instructions", e.target.value)}
                                placeholder={intl.formatMessage({
                                    id: 'bot.instructions.support.placeholder.optional',
                                    defaultMessage: 'Add support topic specific instructions if needed',
                                })}
                                className="topic-instructions"
                                rows={2}
                            />
                        </div>
                    );
                })}
                {missingTopics.length > 0 && (
                    <>
                        {missingTopics.map((topicId, i) => {
                            const isCollapsed = collapsedTopics[topicId] === true;
                            const differenceTitle = intl.formatMessage({
                                defaultMessage: "Support topic not included in this version",
                                id: "supportTopicNotIncludedInVersion"
                            })
                            return (
                                <div key={`missing-${i}`} className={`topic-card missing-topic ${isCollapsed ? 'collapsed' : ''}`}>
                                    <div className={"topic-header"} onClick={() => toggleTopicCollapse(topicId)}>
                                        <div className="topic-toggle">
                                            <ChevronRight
                                                size={16}
                                                style={{
                                                    transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                                                }}
                                            />
                                        </div>
                                        <label>
                                            {getTopicTitle(topicId)}
                                            <VersionDifferenceIndicator differenceTitle={differenceTitle}/>
                                        </label>
                                        <button
                                            className={"remove-material add-material"}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddMissingTopic(topicId);
                                            }}
                                            title={intl.formatMessage({
                                                id: 'bot.materials.add',
                                                defaultMessage: 'Add to this version',
                                            })}
                                        >
                                            <Plus size={22} />
                                        </button>
                                    </div>
                                    <div className="topic-instructions">
                                        {supportTopicCodes?.[topicId]?.instructions || ''}
                                    </div>
                                </div>
                            );
                        })}
                    </>
                )}
                <SupportTopicCreator
                    botData={botData}
                    updateBotData={updateBotData}
                    updateNestedData={updateNestedData}
                />
            </div>
            {deleteConfirmation.isOpen && (
                <Modal
                    header={intl.formatMessage({
                        id: 'remove.topicFromModule',
                        defaultMessage: 'Remove topic from module',
                    })}
                    text={intl.formatMessage({
                        id: 'remove.topicFromModule.description',
                        defaultMessage: 'Are you sure you want to remove this topic from this module?',
                    })}
                    closeModal={closeDeleteConfirmation}
                    confirmAction={removeTopic}
                    confirmText={intl.formatMessage({
                        id: 'remove',
                        defaultMessage: 'Remove',
                    })}
                    alert={true}
                />
            )}
        </>
    );
};

const getTopicDifferenceMessage = (intl, isInstructionsDifferent, isRequestTypeDifferent, isTopicAdded) => {
    const differences = [];

    if (isInstructionsDifferent) {
        differences.push(intl.formatMessage({
            id: 'bot.instructions.difference.instructions',
            defaultMessage: 'Instructions different from default'
        }));
    }

    if (isRequestTypeDifferent) {
        differences.push(intl.formatMessage({
            id: 'bot.instructions.difference.requestType',
            defaultMessage: 'Request type different from default'
        }));
    }

    if (isTopicAdded) {
        differences.push(intl.formatMessage({
            id: 'bot.instructions.difference.topicAdded',
            defaultMessage: 'Topic added in this version'
        }));
    }

    if (differences.length === 0) {
        return intl.formatMessage({
            id: 'bot.instructions.difference.generic',
            defaultMessage: 'Different from default version'
        });
    }

    // Join multiple differences with bullet points
    return differences.join(' • ');
};

export default BotInstructions;